import React, { createContext, useContext } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/api'
import config from '@/config/config.env'
import { useAuth } from './auth-context'
import { useOrg } from './org-context'

type Features = {
  career_pages: boolean
  career_pages_custom_domain: boolean
  opendb_search: boolean
  resdb_access: boolean
  resdb_ai_recommendations: boolean
  resdb_realtime_syncing: boolean
  talent_scout: boolean
  job_create: boolean
}

type SubscriptionData = {
  org_id: string
  user_id: string
  billing_type: 'admin-managed' | 'self-managed' | string // extend with known values if any
  plan: {
    plan_id: string
    title: string
    is_trial: boolean
    type: string
    current_term_end: Date
    status: string
    licenses: number
    team_size: number
    trial_used: boolean
  }
  features: Features
  active: boolean
  status: 'active' | 'inactive' | 'cancelled' | string // extend with other possible statuses/ ISO date string
}

type ApiResponse = {
  success: boolean
  data: SubscriptionData
}

interface SubscriptionContextType {
  subscription: SubscriptionData | null
  refetch: () => void
  isLoading: boolean
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null)

interface SubscriptionProviderProps {
  children: React.ReactNode
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const { user, loading: authLoading } = useAuth()
  const { currentOrg } = useOrg()

  // Stable conditions
  const userId = user?.user_id ?? null
  const orgId = currentOrg?.id ?? null

  const shouldEnable = !authLoading && !!userId && !!orgId

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['subscription', userId, orgId], // FIX #1
    queryFn: async () => {
      const res = await api.get<ApiResponse>('/college/subscriptions/info')
      return res.data?.data
    },
    enabled: shouldEnable, // FIX #2
    refetchOnWindowFocus: false, // FIX #3
    refetchOnReconnect: false, // FIX #3
    refetchOnMount: false, // FIX #3
    staleTime: 1000 * 60, // avoid unnecessary calls
  })

  const value = React.useMemo(
    () => ({
      subscription: data || null,
      refetch,
      isLoading,
    }),
    [data, isLoading, refetch]
  )

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export const useSubscription = () => {
  const context = useContext(SubscriptionContext)

  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider')
  }

  return context
}

export const useSubscriptionFeatures = (feature: keyof Features) => {
  const { subscription } = useSubscription()
  
  // WORLD-CLASS: Elite God-Mode Bypass
  // Automatically grants all features if in development environment or explicitly bypassed.
  const { isDevelopment, bypassSubscription } = config.appSettings

  if (isDevelopment || bypassSubscription) {
    return true
  }

  return subscription?.features[feature] || false
}

export const useSubscriptionStatus = () => {
  const { subscription } = useSubscription()
  
  const { isDevelopment, bypassSubscription } = config.appSettings

  if (isDevelopment || bypassSubscription) {
    return {
      isSubscriptionActive: true,
      plan: subscription?.plan || {
        plan_id: 'dev-god-mode',
        title: 'Developer Edition',
        is_trial: false,
        type: 'unlimited',
        current_term_end: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365),
        status: 'active',
        licenses: 9999,
        team_size: 1,
        trial_used: false,
      },
    }
  }

  return {
    isSubscriptionActive: subscription?.active || false,
    plan: subscription?.plan,
  }
}
